import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClockCircleOutlined } from '@ant-design/icons';
import { AppConstants } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import { removeCacheToken } from '@services/userService';
import { accountActions } from '@store/actions';
import { Button, Modal, Tooltip } from 'antd';

import styles from './SessionTimeout.module.scss';

export const SESSION_TIMEOUT_MS = AppConstants.sessionTimeout * 60 * 1000;
export const SESSION_WARNING_MS = 60 * 1000;

const message = defineMessages({
    timerTooltip: {
        id: 'sessionTimeout.timerTooltip',
        defaultMessage: 'Time remaining before automatic logout',
    },
    title: {
        id: 'sessionTimeout.title',
        defaultMessage: 'Do you want to stay signed in?',
    },
    description: {
        id: 'sessionTimeout.description',
        defaultMessage: 'Your session will end in {seconds} seconds because there has been no activity.',
    },
    stay: {
        id: 'sessionTimeout.stay',
        defaultMessage: 'Stay signed in',
    },
    logout: {
        id: 'sessionTimeout.logout',
        defaultMessage: 'Log out',
    },
});

const formatRemainingTime = (remainingMs) => {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SessionTimeout = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { execute: executeLogout } = useFetch(apiConfig.account.logout);
    const deadlineRef = useRef(Date.now() + SESSION_TIMEOUT_MS);
    const warningOpenRef = useRef(false);
    const logoutStartedRef = useRef(false);
    const lastActivityRef = useRef(0);
    const [ remainingMs, setRemainingMs ] = useState(SESSION_TIMEOUT_MS);
    const [ warningOpen, setWarningOpen ] = useState(false);

    const logout = useCallback(() => {
        if (logoutStartedRef.current) return;
        logoutStartedRef.current = true;
        executeLogout();
        removeCacheToken();
        dispatch(accountActions.logout());
        navigate('/login', { replace: true });
    }, [ dispatch, executeLogout, navigate ]);

    const resetSession = useCallback(() => {
        deadlineRef.current = Date.now() + SESSION_TIMEOUT_MS;
        warningOpenRef.current = false;
        logoutStartedRef.current = false;
        setWarningOpen(false);
        setRemainingMs(SESSION_TIMEOUT_MS);
    }, []);

    const updateTimer = useCallback(() => {
        const nextRemaining = Math.max(0, deadlineRef.current - Date.now());
        setRemainingMs(nextRemaining);

        if (nextRemaining <= 0) {
            logout();
            return;
        }

        if (nextRemaining <= SESSION_WARNING_MS && !warningOpenRef.current) {
            warningOpenRef.current = true;
            setWarningOpen(true);
        }
    }, [ logout ]);

    useEffect(() => {
        const registerActivity = () => {
            if (warningOpenRef.current || document.visibilityState !== 'visible' || !document.hasFocus()) return;

            const now = Date.now();
            if (now - lastActivityRef.current < 1000) return;

            lastActivityRef.current = now;
            deadlineRef.current = now + SESSION_TIMEOUT_MS;
            setRemainingMs(SESSION_TIMEOUT_MS);
        };

        const activityEvents = [ 'pointerdown', 'keydown', 'touchstart', 'scroll' ];
        activityEvents.forEach((eventName) =>
            window.addEventListener(eventName, registerActivity, { passive: true }),
        );
        document.addEventListener('visibilitychange', updateTimer);
        window.addEventListener('focus', updateTimer);

        const timer = window.setInterval(updateTimer, 1000);
        updateTimer();

        return () => {
            window.clearInterval(timer);
            activityEvents.forEach((eventName) => window.removeEventListener(eventName, registerActivity));
            document.removeEventListener('visibilitychange', updateTimer);
            window.removeEventListener('focus', updateTimer);
        };
    }, [ updateTimer ]);

    return (
        <>
            <Tooltip title={intl.formatMessage(message.timerTooltip)}>
                <span className={styles.sessionTimer} aria-label={intl.formatMessage(message.timerTooltip)}>
                    <ClockCircleOutlined />
                    {formatRemainingTime(remainingMs)}
                </span>
            </Tooltip>
            <Modal
                open={warningOpen}
                title={intl.formatMessage(message.title)}
                closable={false}
                keyboard={false}
                maskClosable={false}
                centered
                footer={[
                    <Button key="logout" danger onClick={logout}>
                        {intl.formatMessage(message.logout)}
                    </Button>,
                    <Button key="stay" type="primary" onClick={resetSession}>
                        {intl.formatMessage(message.stay)}
                    </Button>,
                ]}
            >
                {intl.formatMessage(message.description, {
                    seconds: Math.max(0, Math.ceil(remainingMs / 1000)),
                })}
            </Modal>
        </>
    );
};

export default SessionTimeout;
